import type {
	AbschnittCCandidateGender,
	AbschnittCElectionListVoteResult,
	AbschnittCElectionLotResult,
	CandidateId
} from "@/lib/abschnittc/AbschnittCElection";
import type { EvaluatorContext } from "@/lib/abschnittc/impl/EvaluatorContext";
import type { PreliminaryListSpot } from "@/lib/abschnittc/AbschnittCElectionResult";
import { AbschnittCElectionEvaulationInterrupt } from "@/lib/abschnittc/impl/AbschnittCElectionEvaulationInterrupt";
import { arrayEquals } from "@/lib/utility";
import debug from "debug";
import type {
	AbschnittCDirectComparisonSelectResult,
	AbschnittCDirectComparisonTieInfo,
	AbschnittCDirectComparisonTieInfoPoints
} from "@/lib/abschnittc/impl/AbschnittCDirectComparison";

const logger = debug("abschnittc:AbschnittCElectionListVoteResultPreliminaryListReader");

export interface AbschnittCElectionListVoteResultPreliminaryListReaderOptions {
	vote: AbschnittCElectionListVoteResult;
	candidates: CandidateId[];
	gender: AbschnittCCandidateGender;
	context: EvaluatorContext;
	lots: AbschnittCElectionLotResult[];
}

export class AbschnittCElectionListVoteResultPreliminaryListReader {
	private remainingCandidates: CandidateId[];
	private nextSpotId = 1;
	private shifts: { [candidateId: string]: number } = {};
	private scores: { [candidateId: string]: number };

	private currentTieInfo: AbschnittCDirectComparisonTieInfo[] = [];
	private currentSelectResult: AbschnittCDirectComparisonSelectResult | undefined;

	constructor(private options: AbschnittCElectionListVoteResultPreliminaryListReaderOptions) {
		this.remainingCandidates = [...options.candidates];
		this.scores = Object.fromEntries(
			options.candidates.map(id => [id, this.calculateScoreOfCandidate(id)])
		);

		logger(this.scores);
	}

	remainingScores(): { [candidateId: string]: number } {
		return Object.fromEntries(Object.entries(this.scores)
			.filter(([id]) => this.remainingCandidates.includes(id)));
	}

	nextSpot(): PreliminaryListSpot {
		const twoHighest = this.twoHighest();

		if (typeof twoHighest === "undefined") {
			logger(`No two highest candidates found`, this.remainingCandidates);
			throw new Error("No two highest candidates");
		} else if (typeof twoHighest === "string") {
			const spot = this.nextSpotId++;
			const score = this.scores[twoHighest];

			const shift = this.shifts[twoHighest] || 0;
			this.options.context.message("directComparisonResult", {
				type: "last", winner: twoHighest, winnerShift: shift, spot,
				list: this.options.gender
			});
			return {
				spot,
				score,
				shift,
				candidate: twoHighest
			};
		} else { // twoHighest is an array of two candidates
			const winner = this.winnerByDirectComparison(twoHighest);
			const loser = twoHighest.find(id => id !== winner)!;

			const spot = this.nextSpotId++;
			const score = this.scores[winner];

			this.shifts[loser] = this.shifts[loser] ? this.shifts[loser] + 1 : 1;

			let winnerShift = this.shifts[winner] || 0;

			if (!this.currentSelectResult || !this.dcScores)
				throw new Error("No currentSelectResult");
			this.options.context.message("directComparisonResult", {
				type: "pair",
				winner: winner,
				loser: loser,
				list: this.options.gender,
				winnerCount: this.dcScores.winner,
				loserCount: this.dcScores.loser,
				excluded: this.dcScores.excluded,
				equal: this.dcScores.equal,
				spot,
				winnerShift,
				tieInfo: this.currentTieInfo.splice(0, Infinity),
				selectResult: this.currentSelectResult
			});
			this.currentSelectResult = undefined;

			return {
				spot,
				score,
				shift: winnerShift,
				candidate: winner,
				directComparisonOpponent: loser
			};
		}
	}

	calculateScoreOfCandidate(id: CandidateId): number {

		const points: number[] = [];

		logger(this.options.gender);

		for (const vote of Object.entries(this.options.vote.votes)) {
			if (vote[1].list === this.options.gender)
				if (vote[1].rankings[id])
					points.push(vote[1].rankings[id]);
		}

		if (points.length === 0) {
			this.options.context.message("averages", id, 0, 0);
			logger("Candidate has no points");
			return 0;
		}

		const sum = points.reduce((acc, val) => acc + val, 0);
		const score = sum / points.length;

		this.options.context.message("averages", id, score, points.length);

		return score;
	}

	private readonly epsilon = 1e-8;

	equalsWithEpsilon(a: number, b: number): boolean {
		return Math.abs(a - b) < this.epsilon;
	}

	highestAverage(exclude: CandidateId[]) {

		const remaining = this.remainingCandidates.filter(id => !exclude.includes(id));

		if (remaining.length === 0)
			return [];

		if (remaining.length === 1)
			return [...remaining];

		logger(`Calculating highest average of ${remaining.length} candidates`);
		logger("scores", this.remainingScores());

		const scores = Object.entries(this.remainingScores())
			.filter(([id]) => remaining.includes(id))
			.sort((a, b) => b[1] - a[1]);
		const highestScore = scores[0][1];

		const out: CandidateId[] = [];

		for (let i = 0; i < scores.length - 1; i++) {
			if (this.equalsWithEpsilon(scores[i][1], highestScore))
				out.push(scores[i][0]);
			else
				break;
		}

		logger("calculated highest average", out, highestScore);

		return out;
	}

	winnerByPointCount(ids: CandidateId[], score: number): CandidateId[] {

		const votes = this.relevantVotes();

		const count: { [candidateId: string]: number } = {};

		for (const id of ids) {
			count[id] = 0;
		}

		for (const vote of votes) {
			for (const id of ids) {
				if (vote.rankings[id] === score)
					count[id]++;
			}
		}

		this.text(`Im Punktvergleich der Punktzahl ${score} haben die Bewerber*innen ${ids.map(v => this.candidateName(v)).join(", ")} folgende Anzahl an Stimmen erhalten:`);
		for (const id of ids) {
			this.text(`Bewerber*in ${this.candidateName(id)} hat die Punktzahl ${score} auf ${count[id]} Stimmzettel erhalten`);
		}

		const highest = Math.max(...Object.values(count));

		const out: CandidateId[] = [];

		for (const id of ids) {
			if (count[id] === highest)
				out.push(id);
		}

		this.currentTieInfo.push({
			type: "points",
			scores: count,
			digit: score
		});

		return out;
	}

	winnerByPointCounts(ids: CandidateId[]): CandidateId[] {

		let leftInComparison = [...ids];
		let i = 10;
		while (leftInComparison.length > 1 && i > 0) {
			leftInComparison = this.winnerByPointCount(leftInComparison, i);
			i--;
		}

		if (leftInComparison.length === 1)
			this.text(`Die Bewerber*innen ${leftInComparison.map(v => this.candidateName(v)).join(", ")} haben die höchste Anzahl der Punktzahl ${i + 1} erreicht.`);

		return leftInComparison;
	}

	relevantVotes() {
		return Object.entries(this.options.vote.votes)
			.filter(v => v[1].list === this.options.gender)
			.map(value => value[1]);
	}

	private dcScores: { winner: number, loser: number, excluded: number, equal: number } | undefined;

	winnerByDirectComparison(ids: [CandidateId, CandidateId]): CandidateId {

		const votes = this.relevantVotes();

		const left = ids[0];
		const right = ids[1];

		const scores = {
			left: 0,
			right: 0,
			equal: 0,
			excluded: 0
		};

		for (const vote of votes) {
			if (vote.rankings[left] > vote.rankings[right])
				scores.left++;
			else if (vote.rankings[left] < vote.rankings[right])
				scores.right++;
			else if (vote.rankings[left] === vote.rankings[right])
				scores.equal++;
			else
				scores.excluded++;
		}

		this.text(`Die Bewerber ${this.candidateName(left)} und ${this.candidateName(right)} werden direkt miteinander verglichen.`);
		this.text(`${scores.excluded} Stimmzettel wurden vom Vergleich ausgeschlossen. Auf ${scores.equal} Stimmzetteln enthielten beide Bewerber*innen die gleiche Punktzahl.`);

		if (scores.left > scores.right) {
			this.text(`Bewerber*in ${this.candidateName(left)} hat mehr (${scores.left} vs ${scores.right}) direkte Vergleiche gewonnen.`);

			this.dcScores = {
				winner: scores.left, loser: scores.right,
				excluded: scores.excluded, equal: scores.equal
			};

			return left;
		} else if (scores.left < scores.right) {
			this.text(`Bewerber*in ${this.candidateName(right)} hat mehr (${scores.right} vs ${scores.left}) direkte Vergleiche gewonnen.`);

			this.dcScores = {
				winner: scores.right, loser: scores.left,
				excluded: scores.excluded, equal: scores.equal
			};

			return right;
		} else {

			this.text(`Beide Bewerber*innen haben die gleiche Anzahl an direkten Vergleichen gewonnen.`);

			// compare point counts
			const pointCounts = this.winnerByPointCounts(ids);

			if (pointCounts.length === 1) {

				this.dcScores = {
					winner: left === pointCounts[0] ? scores.left : scores.right,
					loser: left === pointCounts[0] ? scores.right : scores.left,
					excluded: scores.excluded,
					equal: scores.equal
				};

				return pointCounts[0];
			} else {

				this.text(`Ein Losentscheid ist erforderlich, da beide Bewerber*innen die gleiche Anzahl an direkten Vergleichen und Punkten erreicht haben.`);
				const lotResult = this.options.lots.find(lot => arrayEquals(
					lot.candidateIds, pointCounts));

				if (!lotResult) {
					throw new AbschnittCElectionEvaulationInterrupt({
						type: "lot-required",
						candidates: pointCounts
					});
				}

				this.dcScores = {
					winner: left === lotResult.winner ? scores.left : scores.right,
					loser: left === lotResult.winner ? scores.right : scores.left,
					excluded: scores.excluded,
					equal: scores.equal
				};

				this.text(`Bewerber*in ${this.candidateName(lotResult.winner)} hat das Los gewonnen.`);

				return lotResult.winner;
			}
		}
	}

	getOrForceLotWinner(candidates: CandidateId[]): CandidateId {
		// lot decides
		const lotResult = this.options.lots.find(lot => arrayEquals(
			lot.candidateIds, candidates));

		if (!lotResult) {
			throw new AbschnittCElectionEvaulationInterrupt({
				type: "lot-required",
				candidates: candidates
			});
		}

		this.currentTieInfo.push({
			type: "lot",
			candidates,
			winner: lotResult.winner
		});

		return lotResult.winner;
	}

	text(message: string) {
		this.options.context.message("text", message);
	}

	candidateName(id: CandidateId) {
		return this.options.context.candidateName(id);
	}

	twoHighest(): CandidateId | [CandidateId, CandidateId] {

		const highest = this.highestAverage([]);

		if (highest.length === 2) {
			// if two candidates have the same score these two are used for direct comparison
			this.text(`Die beiden Beweber*innen ${this.candidateName(highest[0])} und ${this.candidateName(highest[1])} ` +
				`haben die höchste Durchschnittspunktzahl von ${this.scores[highest[0]]} erreicht.`);

			this.currentSelectResult = {
				type: "pair", first: highest[0], second: highest[1],
				firstScore: this.scores[highest[0]], secondScore: this.scores[highest[1]],
				tieInfo: this.currentTieInfo.splice(0, Infinity)
			};

			return highest as [CandidateId, CandidateId];
		} else if (highest.length > 2) {
			logger(`More than two candidates with highest score found`, this.remainingCandidates);

			this.text(`Mehr als zwei Bewerber*innen (${highest.map(v => this.candidateName(v)).join(", ")} haben die `
				+ `höchste Durchschnittspunktzahl von ${this.scores[highest[0]]} erreicht.`);

			const perPoint = this.winnerByPointCounts(highest);

			if (perPoint.length === 2) {
				// if two candidates have the same score these two are used for direct comparison

				this.currentSelectResult = {
					type: "pair", first: perPoint[0], second: perPoint[1],
					firstScore: this.scores[highest[0]], secondScore: this.scores[highest[1]],
					tieInfo: this.currentTieInfo.splice(0, Infinity)
				};

				return perPoint as [CandidateId, CandidateId];
			} else if (perPoint.length === 1) {
				// First candidate has highest points, we can skip averages cause they are identical already

				const withoutHighest = highest.filter(id => id !== perPoint[0]);

				const perPoint2 = this.winnerByPointCounts(withoutHighest);

				if (perPoint2.length === 1) {

					this.currentSelectResult = {
						type: "pair", first: perPoint[0], second: perPoint2[0],
						firstScore: this.scores[highest[0]], secondScore: this.scores[perPoint2[0]],
						tieInfo: this.currentTieInfo.splice(0, Infinity)
					};

					return [perPoint[0], perPoint2[0]];
				} else if (perPoint2.length > 1) {
					const lotWinner = this.getOrForceLotWinner(perPoint2);
					this.currentSelectResult = {
						type: "pair", first: perPoint[0], second: lotWinner,
						firstScore: this.scores[highest[0]], secondScore: this.scores[lotWinner],
						tieInfo: this.currentTieInfo.splice(0, Infinity)
					};

					return [perPoint[0], lotWinner];
				} else {
					throw new Error("No second highest candidate found");
				}

			} else if (perPoint.length === 0) {
				throw new Error("No candidate with highest points found");
			} else if (perPoint.length > 2) {

				const winner = this.getOrForceLotWinner(perPoint);
				const withoutWinner = perPoint.filter(id => id !== winner);
				const winner2 = this.getOrForceLotWinner(withoutWinner);

				this.currentSelectResult = {
					type: "pair", first: winner, second: winner2,
					firstScore: this.scores[winner], secondScore: this.scores[winner2],
					tieInfo: this.currentTieInfo.splice(0, Infinity)
				};

				return [winner, winner2];
			} else {
				throw new Error("No two highest candidates found");
			}

		} else if (highest.length === 0) {
			throw new Error("No candidate with highest average found");
		} else if (highest.length === 1) {
			// if there is only one candidate with the highest score we need to find the second highest
			const secondHighest = this.highestAverage(highest);

			this.text(`Bewerber*in ${this.candidateName(highest[0])} hat die höchste Durchschnittspunktzahl von ${this.scores[highest[0]]} erreicht.`);

			if (secondHighest.length === 0) {
				// if there is no second highest candidate we return the highest candidate

				this.text(`Es ist nur ein Bewerber verbleibend.`);
				return highest[0];
			} else if (secondHighest.length === 1) {
				// if there is a single second highest candidate we return the two highest candidates
				this.text(`Bewerber*in ${this.candidateName(secondHighest[0])} hat die zweithöchste Durchschnittspunktzahl von ${this.scores[secondHighest[0]]} erreicht.`);

				this.currentSelectResult = {
					type: "pair", first: highest[0], second: secondHighest[0],
					firstScore: this.scores[highest[0]], secondScore: this.scores[secondHighest[0]],
					tieInfo: this.currentTieInfo.splice(0, Infinity)
				};

				return [highest[0], secondHighest[0]];
			} else {
				// if there are multiple second highest candidates we need to compare them by points
				this.text(`Mehrere Bewerber*innen (${secondHighest.map(v => this.candidateName(v)).join(", ")} haben die zweithöchste Durchschnittspunktzahl von ${this.scores[secondHighest[0]]} erreicht.`);

				const perPoint = this.winnerByPointCounts(secondHighest);

				if (perPoint.length === 1) {

					this.currentSelectResult = {
						type: "pair", first: highest[0], second: perPoint[0],
						firstScore: this.scores[highest[0]], secondScore: this.scores[perPoint[0]],
						tieInfo: this.currentTieInfo.splice(0, Infinity)
					};

					return [highest[0], perPoint[0]];
				} else if (perPoint.length === 0) {
					throw new Error("No second highest candidate found");
				} else {
					const winner = this.getOrForceLotWinner(perPoint);

					this.currentSelectResult = {
						type: "pair", first: highest[0], second: winner,
						firstScore: this.scores[highest[0]], secondScore: this.scores[winner],
						tieInfo: this.currentTieInfo.splice(0, Infinity)
					};

					return [highest[0], winner];
				}
			}
		} else {
			throw new Error("No two highest candidates found");
		}
	}

	list(): PreliminaryListSpot[] {

		const spots: PreliminaryListSpot[] = [];

		while (this.remainingCandidates.length > 0) {

			this.text(`Es sind noch ${this.remainingCandidates.length} Bewerber*innen übrig.`);
			const next = this.nextSpot();
			spots.push(next);
			this.remainingCandidates.splice(this.remainingCandidates.indexOf(next.candidate), 1);
			this.text(`Bewerber*in ${this.candidateName(next.candidate)} erhält den Listenplatz ${next.spot}.`);
		}

		return spots;
	}
}
